import HeaderWordmark from '../shared/HeaderWordmark';
import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import AppNav from '../shared/AppNav';
import useBranchesData from '../../hooks/useBranchesData';
import useAppChrome from '../../hooks/useAppChrome';
import useDismiss from '../../hooks/useDismiss';
import { useUiV2 } from '../../config/uiVersion';
import UnitDetailPane from '../InventoryDashboard/UnitDetailPane';
import BranchCombinedView from './BranchCombinedView';
import MFCoverageView from './MFCoverageView';
import CategoryTrendsView from './CategoryTrendsView';
import BranchesPageV2 from '../BranchesV2/BranchesPageV2';
import SOURCE_LABELS from '../../config/sourceLabels';
import './BranchOverview.css';

/**
 * /branches: the redesigned page with the "new design" flag on, today's page
 * otherwise. Both read their data through hooks/useBranchesData and the
 * shared utils (branchView, categoryTrends, listingCoverage).
 */
export default function BranchOverview(props) {
    const uiV2 = useUiV2();
    return uiV2 ? <BranchesPageV2 /> : <BranchOverviewV1 {...props} />;
}

BranchOverview.propTypes = {
    onLogout: PropTypes.func.isRequired,
    user: PropTypes.shape({
        name: PropTypes.string,
        username: PropTypes.string,
    }),
    orgName: PropTypes.string,
};

BranchOverview.defaultProps = {
    user: null,
};

function BranchOverviewV1({ onLogout, user, orgName }) {
    // Same orientation cue as the other two headers: the dealership whose data
    // is on screen, which for SAG staff is not their own org.
    // (hooks/useAppChrome, shared with the v2 top bar).
    const { dealership, readOnly } = useAppChrome({ orgName });
    // With the new design on, the v2 top bar (AppShell/AppTopBar) is the header.
    const uiV2 = useUiV2();
    const navigate = useNavigate();
    const [activeView, setActiveView] = useState('overview');

    // Inventory on mount, each view's payload lazily on first show, the
    // analysis window (hooks/useBranchesData, shared with the v2 page).
    const {
        invUnits, invByBranch, inventoryLoading,
        combinedData, combinedLoading, combinedError,
        matrixData, matrixLoading, matrixError, retryMatrix,
        trendsData, trendsLoading, trendsError,
        trendDateFrom, trendDateTo, applyTrendWindow,
    } = useBranchesData(activeView);

    const [selectedUnit, setSelectedUnit] = useState(null);

    const [showUserMenu, setShowUserMenu] = useState(false);
    const userMenuRef = useRef(null);

    const closeUserMenu = useCallback(() => setShowUserMenu(false), []);
    useDismiss(userMenuRef, closeUserMenu);

    const displayName = user?.name || user?.username || 'Admin';
    const userInitial = displayName[0].toUpperCase();

    return (
        <div className="branch-overview">
            {!uiV2 && (
                <header className="dashboard-header">
                    <div className="header-left">
                        <HeaderWordmark />
                        <div>
                            <p className="subtitle">Inventory Intelligence</p>
                        </div>
                        {/* Same orientation cue as the dashboard header — which
                            dealership's branches these are. */}
                        {dealership && <span className="header-org" title="Dealership currently in view">{dealership}</span>}
                        {readOnly && (
                            <span className="header-readonly" title="You can look at this dealership's data but not change it">
                                <span className="header-readonly-dot" aria-hidden="true" />
                                Read-only
                            </span>
                        )}
                    </div>
                    <div className="header-right">
                        <AppNav current="branches" />
                        <div className="user-menu-wrapper" ref={userMenuRef}>
                            <button
                                className="btn-header btn-user"
                                onClick={() => setShowUserMenu((v) => !v)}
                                aria-expanded={showUserMenu}
                            >
                                <span className="user-avatar">{userInitial}</span>
                                <span className="user-name">{displayName}</span>
                                <svg className={`chevron ${showUserMenu ? 'open' : ''}`} viewBox="0 0 20 20" fill="currentColor" width="12" height="12">
                                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </button>
                            {showUserMenu && (
                                <div className="user-dropdown">
                                    <div className="dropdown-divider" />
                                    <button className="dropdown-item dropdown-logout" onClick={onLogout}>
                                        <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
                                            <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
                                        </svg>
                                        Sign Out
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </header>
            )}

            <div className="bo-tab-section">
                <span className="bo-tab-section-label">Inventory Intelligence</span>
                <div className="bo-view-bar">
                    <button
                        className={`bo-view-btn ${activeView === 'overview' ? 'bo-view-active' : ''}`}
                        onClick={() => setActiveView('overview')}
                    >
                        Overview
                    </button>
                    <button
                        className={`bo-view-btn ${activeView === 'coverage' ? 'bo-view-active' : ''}`}
                        onClick={() => setActiveView('coverage')}
                    >
                        {SOURCE_LABELS.listingCoverageGap}
                    </button>
                    <button
                        className={`bo-view-btn ${activeView === 'category' ? 'bo-view-active' : ''}`}
                        onClick={() => setActiveView('category')}
                    >
                        Category Trends
                    </button>
                </div>
            </div>

            <main className="bo-main">
                {activeView === 'overview' && (
                    <BranchCombinedView
                        data={combinedData}
                        loading={combinedLoading}
                        error={combinedError}
                        navigate={navigate}
                        invByBranch={invByBranch}
                        invUnits={invUnits}
                        inventoryLoading={inventoryLoading}
                    />
                )}
                {activeView === 'coverage' && (
                    <MFCoverageView
                        invUnits={invUnits}
                        matrixData={matrixData}
                        matrixLoading={matrixLoading}
                        matrixError={matrixError}
                        onRetry={retryMatrix}
                        onOpenUnit={setSelectedUnit}
                    />
                )}
                {activeView === 'category' && (
                    <CategoryTrendsView
                        trendsData={trendsData}
                        trendsLoading={trendsLoading}
                        trendsError={trendsError}
                        dateFrom={trendDateFrom}
                        dateTo={trendDateTo}
                        onDateChange={applyTrendWindow}
                    />
                )}
            </main>

            {selectedUnit && (
                <UnitDetailPane
                    unit={selectedUnit}
                    onClose={() => setSelectedUnit(null)}
                />
            )}
        </div>
    );
}

BranchOverviewV1.propTypes = {
    onLogout: PropTypes.func.isRequired,
    user: PropTypes.shape({
        name: PropTypes.string,
        username: PropTypes.string,
    }),
    orgName: PropTypes.string,
};

BranchOverviewV1.defaultProps = {
    user: null,
};
